import React, { useEffect } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { Layout, Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '@shared/ErrorBoundary';
import { ReceiptPutawayProvider, useReceiptPutaway } from '@contexts/operator/ReceiptPutawayContext';
import PutawayHeader from '@components/operator/receipts/putaway/PutawayHeader';
import PutawayTable from '@components/operator/receipts/putaway/PutawayTable';
import PutawayFooter from '@components/operator/receipts/putaway/PutawayFooter';
import BatchApplyBar from '@components/operator/receipts/putaway/BatchApplyBar';

const { Content } = Layout;

function PutawayContent() {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { state } = useReceiptPutaway();

  // Full-bleed layout
  useEffect(() => {
    const el = document.querySelector('.operator-content');
    el?.classList.add('operator-content--full-bleed');
    return () => el?.classList.remove('operator-content--full-bleed');
  }, []);

  if (!state.receipt) {
    return <Alert type="warning" message={t('common.no_data', { defaultValue: '無資料' })} showIcon />;
  }

  // Defense-in-depth：Web Putaway 頁面僅適用 Dynamic 倉庫；Virtual/Dedicated 應走 auto_putaway。
  if (state.receipt.warehouse && state.receipt.warehouse.storage_mode !== 'dynamic') {
    return (
      <Alert
        type="warning"
        showIcon
        message={t('operator.pages.putaway.warnings.non_dynamic_warehouse_title', { defaultValue: '此倉庫不支援手動上架' })}
        description={t('operator.pages.putaway.warnings.non_dynamic_warehouse_body', { defaultValue: 'Virtual / Dedicated 倉庫會自動上架，不需要手動配架。' })}
      />
    );
  }

  const handleAfterSubmit = (payload) => {
    // Refetch receipt so putaway_quantity and status reflect latest
    if (payload.success_count > 0) {
      revalidator.revalidate();
    }
  };

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', overflow: 'hidden', background: '#f5f5f5' }}>
      <ErrorBoundary homeUrl="/operator"><PutawayHeader /></ErrorBoundary>
      <ErrorBoundary homeUrl="/operator"><BatchApplyBar /></ErrorBoundary>
      <Content style={{ overflow: 'auto', padding: '16px 24px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            overflow: 'hidden',
          }}>
            <ErrorBoundary homeUrl="/operator"><PutawayTable /></ErrorBoundary>
          </div>
        </div>
      </Content>
      <ErrorBoundary homeUrl="/operator"><PutawayFooter onAfterSubmit={handleAfterSubmit} /></ErrorBoundary>
    </Layout>
  );
}

// Re-sync context when loader data changes after revalidation
function ProviderBridge({ receipt, children }) {
  return (
    <ReceiptPutawayProvider key={receipt?.id + ':' + (receipt?.total_putaway_quantity || 0)} initialReceipt={receipt}>
      {children}
    </ReceiptPutawayProvider>
  );
}

export default function ReceiptPutawayPage() {
  const receipt = useLoaderData();

  return (
    <ErrorBoundary homeUrl="/operator">
      <ProviderBridge receipt={receipt}>
        <PutawayContent />
      </ProviderBridge>
    </ErrorBoundary>
  );
}
